import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { OverlayTrigger, LinkToNode, Spinner } from "components/common";
import { Badge } from "components/bootstrap";
import InputStateComparator from "logic/inputs/InputStateComparator";
import { NodesStore } from "stores/nodes/NodesStore";
import { useStore } from "stores/connect";
import StringUtils from "util/StringUtils";
const comparator = new InputStateComparator();
const getBadgeColorForState = (sortedStates, input, nodes, isOnlyOnePerCluster) => {
  const nodesWithKnownState = sortedStates.reduce((numberOfNodes, state2) => numberOfNodes + state2.count, 0);
  if (input.global && !isOnlyOnePerCluster && nodesWithKnownState !== Object.keys(nodes).length) {
    return "warning";
  }
  const { state } = sortedStates[0];
  switch (state) {
    case "RUNNING":
      return "success";
    case "FAILED":
    case "STOPPED":
      return "danger";
    case "STARTING":
      return "primary";
    default:
      return "warning";
  }
};
const stateLabel = (state) => i18n.t(`input_states.${state}`, { ns: "inputs", defaultValue: StringUtils.toTitleCase(state) });
const getTextForState = (sortedStates, input) => input.global ? sortedStates.map(
  (state) => i18n.t("input_states.count", { ns: "inputs", count: state.count, state: stateLabel(state.state) })
).join(", ") : stateLabel(sortedStates[0].state);
const InputStateBadge = ({ input, inputStates = void 0 }) => {
  var _a;
  const { t } = useTranslation("inputs");
  const { nodes } = useStore(NodesStore);
  const isLoading = !(inputStates && nodes);
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const inputId = input.id;
  const sortedInputStates = {};
  if (inputStates[inputId]) {
    Object.keys(inputStates[inputId]).forEach((node) => {
      const { state } = inputStates[inputId][node];
      if (!sortedInputStates[state]) {
        sortedInputStates[state] = [];
      }
      sortedInputStates[state].push(node);
    });
  }
  const sorted = Object.keys(sortedInputStates).sort(comparator.compare.bind(comparator)).map((state) => ({
    state,
    count: sortedInputStates[state].length
  }));
  const isOnlyOnePerCluster = Object.values((_a = inputStates[inputId]) != null ? _a : {}).some(
    (nodeState) => nodeState.only_one_per_cluster
  );
  if (sorted.length > 0) {
    const popOverText = sorted.map(
      (state) => sortedInputStates[state.state].map((node) => /* @__PURE__ */ React.createElement("small", { key: `${input.id}-state-${state.state}-node-${node}` }, /* @__PURE__ */ React.createElement(LinkToNode, { nodeId: node }), ": ", StringUtils.toTitleCase(state.state), /* @__PURE__ */ React.createElement("br", null)))
    );
    return /* @__PURE__ */ React.createElement(
      OverlayTrigger,
      {
        trigger: "hover",
        placement: "bottom",
        overlay: popOverText,
        rootClose: true,
        title: t("input_states.title", { title: input.title })
      },
      /* @__PURE__ */ React.createElement(
        Badge,
        {
          color: getBadgeColorForState(sorted, input, nodes, isOnlyOnePerCluster),
          variant: "light",
          dot: true,
          style: { cursor: "pointer" }
        },
        getTextForState(sorted, input)
      )
    );
  }
  const text = StringUtils.toTitleCase(input.global || input.node === void 0 ? "0 RUNNING" : "NOT RUNNING");
  return /* @__PURE__ */ React.createElement(Badge, { color: "warning", variant: "light", dot: true }, text);
};
export default InputStateBadge;
